import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { setPageSeo } from '../utils/seo';
import { Phone, Feather, Mail } from 'lucide-react';
import './ThankYou.css';

function ThankYou() {
  useEffect(() => {
    setPageSeo({
      title: "Your Guide Is On Its Way | Binding Thread",
      description: "Thank you for downloading our free guide. Now discover how Binding Thread can preserve your mum's spoken memories as keepsake letters.",
      canonical: 'https://www.bindingthread.com.au/thank-you'
    });
  }, []);

  return (
    <div className="ty-page" data-testid="thank-you-page">

      {/* Green Header */}
      <header className="ty-header">
        <div className="ty-header-inner">
          <Link to="/" className="ty-home-link">Home</Link>
          <Link to="/" className="ty-logo-link">
            <img src="/logo-final.png" alt="Binding Thread logo" className="ty-logo" width="260" height="70" />
          </Link>
          <div className="ty-header-spacer"></div>
        </div>
      </header>

      {/* Thank You + Headline */}
      <section className="ty-hero" data-testid="ty-hero">
        <div className="ty-container">
          <p className="ty-thank-you">Thank You</p>
          <h1 className="ty-headline">The journey has begun.</h1>
          <p className="ty-subtitle">
            Your guide, <em>&ldquo;Before It&apos;s Too Late: Ask Your Mum These 10 Questions,&rdquo;</em> is on its way to your inbox.
          </p>
        </div>
      </section>

      {/* Central Image */}
      <section className="ty-image-section">
        <div className="ty-container">
          <img
            src="https://images.unsplash.com/photo-1511771983947-d0a43a94f18b?w=900&q=80&fm=webp"
            alt="Elegant letter envelopes resting on a wooden surface"
            className="ty-central-image"
            width="900"
            height="400"
          />
        </div>
      </section>

      {/* Bridge Content */}
      <section className="ty-bridge" data-testid="ty-bridge">
        <div className="ty-container ty-bridge-inner">
          <p>
            You now have the keys to unlock stories that have waited a lifetime to be told.
            But for most of us, life gets busy. We ask the questions, but we don&apos;t always
            record the answers. We listen, but we don&apos;t always write them down.
          </p>
          <p>
            Without a medium to preserve them, even the most beautiful stories eventually fade.
          </p>
        </div>
      </section>

      {/* Mother's Day Offer */}
      <section className="ty-offer" data-testid="ty-offer">
        <div className="ty-container ty-offer-inner">
          <h2 className="ty-offer-title">Give Her the Invitation of a Lifetime.</h2>
          <div className="ty-gold-divider"></div>
          <p className="ty-offer-body">
            For Mother&apos;s Day, don&apos;t just ask the questions &mdash; give her the medium
            to archive them forever. We have <strong>69 spots remaining</strong> in our
            Mother&apos;s Day cohort at our special introductory rate.
          </p>
          <p className="ty-offer-body">
            We will send your mum a physical, personalised invitation on our archival 176gsm parchment, inviting her to share her legacy at her own pace.
          </p>
          <Link to="/checkout" className="ty-btn" data-testid="ty-cta-shop">
            Secure Her Mother&apos;s Day Spot &ndash; $69
          </Link>
        </div>
      </section>

      {/* Process Icons */}
      <section className="ty-process" data-testid="ty-process">
        <div className="ty-container">
          <div className="ty-process-row">
            <div className="ty-process-item">
              <div className="ty-process-icon">
                <Phone size={30} />
              </div>
              <p>She calls to share a memory.</p>
            </div>
            <div className="ty-process-connector"></div>
            <div className="ty-process-item">
              <div className="ty-process-icon">
                <Feather size={30} />
              </div>
              <p>We transform her voice into a keepsake letter.</p>
            </div>
            <div className="ty-process-connector"></div>
            <div className="ty-process-item">
              <div className="ty-process-icon">
                <Mail size={30} />
              </div>
              <p>You receive a piece of history every month.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Upsell */}
      <section className="ty-upsell" data-testid="ty-upsell">
        <div className="ty-container">
          <p className="ty-upsell-text">
            Once you join, you will have the opportunity to add our specialised
            <strong> A4 Heritage Tin</strong> to your order &mdash; the perfect sanctuary
            for her collection of keepsake letters as they arrive.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="ty-footer">
        <div className="ty-footer-inner">
          <div className="ty-footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms &amp; Conditions</Link>
            <Link to="/">bindingthread.com.au</Link>
          </div>
          <p className="ty-footer-copy">&copy; {new Date().getFullYear()} Binding Thread. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default ThankYou;
